/**
 * usePatientClinicalMemory
 *
 * React hook that loads and exposes a patient's cached clinical memory.
 *
 *   const { memory, loading, error, isStale, refresh } =
 *     usePatientClinicalMemory(patientId, practiceId);
 *
 * - Calls GET /api/clinical-memory/:patientId on mount and whenever the
 *   identifiers change.
 * - When the response indicates indexStatus === "INDEXING" or "STALE", the
 *   hook polls every 4 seconds until the doc settles to INDEXED / FAILED /
 *   NOT_INDEXED. Polling stops on unmount.
 * - `refresh()` POSTs to /api/clinical-memory/:patientId/refresh and starts
 *   polling. Resolves immediately — the actual rebuild happens in the worker.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  getPatientClinicalMemory,
  refreshPatientClinicalMemory,
} from "../api";

const POLL_MS = 4000;
const ACTIVELY_BUILDING_STATUSES = new Set(["INDEXING", "STALE"]);

export function usePatientClinicalMemory(patientId, practiceId) {
  const [memory, setMemory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const pollTimerRef = useRef(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, []);

  const fetchOnce = useCallback(
    async ({ silent = false } = {}) => {
      if (!patientId) return null;
      if (!silent) setLoading(true);
      setError(null);
      try {
        const data = await getPatientClinicalMemory(patientId, practiceId);
        if (!mountedRef.current) return data;
        setMemory(data);
        return data;
      } catch (err) {
        if (!mountedRef.current) return null;
        const msg =
          err?.response?.data?.error ||
          err?.message ||
          "Failed to load clinical memory";
        setError(msg);
        return null;
      } finally {
        if (mountedRef.current && !silent) setLoading(false);
      }
    },
    [patientId, practiceId],
  );

  const schedulePoll = useCallback(() => {
    if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    pollTimerRef.current = setTimeout(async () => {
      if (!mountedRef.current) return;
      const data = await fetchOnce({ silent: true });
      if (data && ACTIVELY_BUILDING_STATUSES.has(data.indexStatus)) {
        schedulePoll();
      }
    }, POLL_MS);
  }, [fetchOnce]);

  // Initial load + auto-poll when STALE / INDEXING
  useEffect(() => {
    if (!patientId) {
      setMemory(null);
      setError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const data = await fetchOnce();
      if (cancelled) return;
      if (data && ACTIVELY_BUILDING_STATUSES.has(data.indexStatus)) {
        schedulePoll();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId, practiceId, fetchOnce, schedulePoll]);

  const refresh = useCallback(async () => {
    if (!patientId) return;
    try {
      await refreshPatientClinicalMemory(patientId, practiceId);
      // Re-fetch immediately to reflect STALE status, then poll until settled.
      await fetchOnce({ silent: true });
      schedulePoll();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "Failed to queue refresh";
      setError(msg);
    }
  }, [patientId, practiceId, fetchOnce, schedulePoll]);

  const isStale =
    !!memory && ACTIVELY_BUILDING_STATUSES.has(memory.indexStatus);

  return { memory, loading, error, isStale, refresh };
}

export default usePatientClinicalMemory;
